import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Sparkles, Info, CheckCircle2, AlertCircle, ShieldCheck, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import MatchFieldsSection from "@/components/employer/MatchFieldsSection";

/**
 * Employer ▸ Post a Job.
 *
 * Sends to POST /employer/jobs — the job lands ``pending_review`` and is
 * hidden from the public board until a Simonara admin approves it. The
 * formatting hints on the right rail are lifted directly from the SEO spec
 * (title convention, description shape, tag choices) so what employers see
 * matches what admins actually approve.
 */
// iter 149.4 — Pay-transparency nudge lookup. Kept at module scope so it's
// not reallocated on every PostJob render. States with statewide
// employer-facing pay-range disclosure statutes as of Feb 2026:
//   CA (Cal. Lab. Code §432.3) · CO (Equal Pay for Equal Work Act) ·
//   NY (Labor Law §194-b)      · WA (RCW 49.58.110)
// (Local ordinances like NYC/Ithaca/Jersey City are covered by NY.)
const PAY_RANGE_STATES = { CA: "California", CO: "Colorado", NY: "New York", WA: "Washington" };

const INITIAL = {
    title: "", location: "", employment_type: "Full-time", remote_status: "on_site",
    industry: "General", experience_level: "mid",
    pay_mode: "range",   // iter 149.3 — required disclosure: range | starting_at | doe
    salary_min: "", salary_max: "", salary_currency: "USD", salary_period: "YEAR",
    description: "", benefits: "", apply_url: "",
};

// Client-side heuristic scorer — mirrors Google-for-Jobs required-fields
// checks. Not authoritative (admin still reviews) but pushes employers to
// good posting hygiene before submit.
function scoreDraft(f) {
    const checks = [];
    const push = (id, ok, weight, label, tip) => checks.push({ id, ok, weight, label, tip });

    const wordCount = (f.description || "").trim().split(/\s+/).filter(Boolean).length;
    push("title", !!f.title && f.title.length <= 80, 15, "Concise job title", "Keep the title under 80 chars — 'Level + Role Name' only.");
    push("location", !!f.location, 15, "Location filled", "City, ST or 'Remote' — matches Google's schema.");
    push("emptype", !!f.employment_type, 5, "Employment type set", null);
    push("desc", wordCount >= 150, 20, `Description ≥ 150 words (${wordCount})`, "Aim 250-800 words — Overview → Responsibilities → Qualifications → About us.");
    const salMin = parseInt(f.salary_min, 10) || 0;
    const salMax = parseInt(f.salary_max, 10) || 0;
    // iter 149.3 — score pay disclosure by mode. Range still scores highest
    // because Google for Jobs boosts it the most; "starting at" is still a
    // valid disclosure; DOE is honest but earns fewer SEO points.
    let payOk = false;
    let payLabel = "Pay disclosure provided";
    let payTip = "Google-for-Jobs boosts postings with min AND max salary significantly.";
    if (f.pay_mode === "range") {
        payOk = salMin > 0 && salMax > 0 && salMax >= salMin;
        payLabel = "Salary range provided";
    } else if (f.pay_mode === "starting_at") {
        payOk = salMin > 0;
        payLabel = "Starting salary provided";
        payTip = "Consider a full range instead — Google for Jobs favours min + max.";
    } else if (f.pay_mode === "doe") {
        payOk = true;   // honest disclosure counts
        payLabel = "Pay marked 'Depending on Experience'";
        payTip = "A concrete range or starting amount ranks higher on Google for Jobs.";
    }
    push("salary", payOk, 25, payLabel, payTip);
    const benefitsCount = (f.benefits || "").split("\n").map((s) => s.trim()).filter(Boolean).length;
    push("benefits", benefitsCount >= 1, 10, `Benefits listed (${benefitsCount})`, "Bullet a few concrete benefits — they render as chips on the public page.");
    push("remote", !!f.remote_status, 5, "Remote status chosen", null);
    push("industry", !!f.industry && f.industry !== "General", 5, "Industry set (non-generic)", "Set a specific industry — improves matching + SEO taxonomy.");

    const earned = checks.filter((c) => c.ok).reduce((s, c) => s + c.weight, 0);
    const total = checks.reduce((s, c) => s + c.weight, 0);
    const score = Math.round((earned / total) * 100);
    const tone = score >= 80 ? "green" : score >= 60 ? "amber" : "rose";
    return { score, tone, checks, payOk };
}

const TONE_STYLES = {
    green: { text: "text-emerald-700 dark:text-emerald-400", bg: "bg-emerald-500/10", ring: "ring-emerald-500/30", label: "Great — highly discoverable" },
    amber: { text: "text-amber-700 dark:text-amber-400", bg: "bg-amber-500/10", ring: "ring-amber-500/30", label: "Getting there — add a few missing bits" },
    rose: { text: "text-rose-700 dark:text-rose-400", bg: "bg-rose-500/10", ring: "ring-rose-500/30", label: "Fill in more — SEO reach will be weak" },
};

export default function PostJob({ initial, initialSkills, initialKnockouts, fromIntake }) {
    const nav = useNavigate();
    const [f, setF] = useState(initial || INITIAL);
    const [saving, setSaving] = useState(false);
    // iter-168 — Browse/Match v1 fields.
    const [requiredSkills, setRequiredSkills] = useState(initialSkills || []);
    const [knockoutQuestions, setKnockoutQuestions] = useState(initialKnockouts || []);
    const seo = useMemo(() => scoreDraft(f), [f]);
    const tone = TONE_STYLES[seo.tone];

    const update = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

    // iter 149.4 — Pay-transparency reminder. If the employer picks
    // "Depending on Experience" for a role located in a state with a
    // mandatory pay-range disclosure statute, surface a compliance nudge
    // inline (non-blocking — we don't know their exact legal setup;
    // the reminder is a courtesy). Keyed on the 2-letter state code
    // parsed out of the free-text `location` field. `PAY_RANGE_STATES`
    // is defined at module scope above.
    const detectedState = (() => {
        const loc = (f.location || "").trim();
        if (!loc) return null;
        // Match a trailing ", XX" or " XX " (2-letter code) OR the full
        // state name for the four covered states.
        const codeMatch = loc.match(/\b([A-Z]{2})\b\s*(?:$|,|\s\d)/);
        if (codeMatch && PAY_RANGE_STATES[codeMatch[1]]) return codeMatch[1];
        const lower = loc.toLowerCase();
        for (const [code, name] of Object.entries(PAY_RANGE_STATES)) {
            if (lower.includes(name.toLowerCase())) return code;
        }
        return null;
    })();
    const showTransparencyNudge = f.pay_mode === "doe" && !!detectedState;

    // iter 149.6 — Salary insights tooltip. Debounced lookup against
    // /api/employer/salary-insights returns the median midpoint from
    // recent Simonara jobs matching the same title token + location.
    // Renders "Median in your area: $X" ONLY when we have at least 3
    // comparable rows — "Not enough data yet" otherwise.
    const [insight, setInsight] = useState(null);
    const [insightLoading, setInsightLoading] = useState(false);
    useEffect(() => {
        // Only meaningful when the employer is filling numeric fields.
        if (f.pay_mode === "doe" || !f.title || f.title.length < 3) {
            setInsight(null);
            return;
        }
        const handle = setTimeout(async () => {
            setInsightLoading(true);
            try {
                const { data } = await api.get("/employer/salary-insights", {
                    params: { title: f.title, location: f.location || "", period: f.salary_period || "YEAR" },
                });
                setInsight(data);
            } catch {
                setInsight(null);
            } finally {
                setInsightLoading(false);
            }
        }, 450);
        return () => clearTimeout(handle);
    }, [f.title, f.location, f.salary_period, f.pay_mode]);

    const insightBadge = (() => {
        if (f.pay_mode === "doe") return null;
        if (insightLoading) return "Checking Simonara medians…";
        if (!insight) return null;
        if (insight.median) {
            const short = insight.median >= 1000
                ? `$${(insight.median / 1000).toFixed(insight.median % 1000 === 0 ? 0 : 1)}k`
                : `$${insight.median}`;
            const per = (insight.period || "YEAR").toUpperCase();
            const suffix = per === "HOUR" ? "/hr" : per === "MONTH" ? "/mo" : per === "DAY" ? "/day" : "";
            return `Median for similar Simonara roles${f.location ? " in your area" : ""}: ${short}${suffix} · sample ${insight.sample}`;
        }
        return "Not enough comparable Simonara jobs yet — your call.";
    })();

    const submit = async (e) => {
        e.preventDefault();
        // iter 149.3 — client-side pay-mode guard so the user gets an
        // immediate error instead of a round-trip 400.
        if (!seo.payOk) {
            toast.error("A pay range is required by California law.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: f.title.trim(),
                company: "",   // backend forces this from the employer profile
                location: f.location.trim(),
                employment_type: f.employment_type,
                remote_status: f.remote_status,
                industry: f.industry,
                experience_level: f.experience_level,
                pay_mode: f.pay_mode,
                salary_min: f.pay_mode !== "doe" && f.salary_min ? parseInt(f.salary_min, 10) : null,
                salary_max: f.pay_mode === "range" && f.salary_max ? parseInt(f.salary_max, 10) : null,
                salary_currency: f.salary_currency,
                salary_period: f.salary_period,
                description: f.description,
                benefits: f.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
                apply_url: f.apply_url.trim(),
                is_active: true,
                // iter-168 — Browse/Match v1 fields
                required_skills: requiredSkills,
                knockout_questions: knockoutQuestions,
                // iter-172 — link the publish back to the parsed intake so
                // Ops can see which parse this role came from.
                ...(fromIntake ? { source_intake_id: fromIntake } : {}),
            };
            const { data } = await api.post("/employer/jobs", payload);
            toast.success(data.message || "Submitted for Simonara review.");
            nav("/employer/jobs");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="employer-post-job" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <form onSubmit={submit} className="lg:col-span-2 space-y-4">
                <div>
                    <p className="overline">{fromIntake ? "Employer · Review parsed role" : "Employer · Post a Job"}</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        {fromIntake ? "Review and confirm" : "Draft a new role"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {fromIntake
                            ? "Simonara AI parsed your job description. Review and edit every field — nothing publishes until you confirm."
                            : "Every job is reviewed by Simonara before it appears on the public board and Google for Jobs. Follow the formatting tips on the right for the best SEO reach."}
                    </p>
                </div>

                {fromIntake && (
                    <div
                        data-testid="post-job-review-banner"
                        className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-foreground inline-flex items-start gap-2"
                    >
                        <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                        <span>Parsed from your intake. Edit anything that looks off, then confirm to publish.</span>
                    </div>
                )}

                <div className="card-soft p-5 space-y-3">
                    <div>
                        <Label htmlFor="title">Job title <span className="text-muted-foreground text-xs">(SEO-critical)</span></Label>
                        <Input id="title" data-testid="post-job-title" value={f.title} onChange={update("title")} placeholder="e.g., Senior HR Business Partner" required className="mt-1.5" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="loc">Location</Label>
                            <Input id="loc" data-testid="post-job-location" value={f.location} onChange={update("location")} placeholder="Los Angeles, CA" required className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Remote status</Label>
                            <select data-testid="post-job-remote" value={f.remote_status} onChange={update("remote_status")} className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm">
                                <option value="on_site">On-site</option>
                                <option value="hybrid">Hybrid</option>
                                <option value="remote">Remote</option>
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Employment type</Label>
                            <select data-testid="post-job-emptype" value={f.employment_type} onChange={update("employment_type")} className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm">
                                <option>Full-time</option><option>Part-time</option><option>Contract</option><option>Temporary</option><option>Intern</option>
                            </select>
                        </div>
                        <div>
                            <Label>Experience level</Label>
                            <select data-testid="post-job-exp" value={f.experience_level} onChange={update("experience_level")} className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm">
                                <option value="entry">Entry</option><option value="mid">Mid</option><option value="senior">Senior</option><option value="lead">Lead</option><option value="executive">Executive</option>
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>Industry</Label>
                            <select data-testid="post-job-industry" value={f.industry} onChange={update("industry")} className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm">
                                <option value="General">General (not set)</option>
                                <option>Engineering</option>
                                <option>Design</option>
                                <option>Product</option>
                                <option>Sales</option>
                                <option>Marketing</option>
                                <option>Customer Success</option>
                                <option>Operations</option>
                                <option>People &amp; HR</option>
                                <option>Finance</option>
                                <option>Data &amp; Analytics</option>
                                <option>Legal</option>
                                <option>Healthcare</option>
                                <option>Hospitality</option>
                                <option>Retail</option>
                                <option>Manufacturing</option>
                                <option>Education</option>
                                <option>Non-profit</option>
                            </select>
                        </div>
                        <div>
                            <Label>Pay period</Label>
                            <select data-testid="post-job-salperiod" value={f.salary_period} onChange={update("salary_period")} className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm">
                                <option value="YEAR">Annual (USD/year)</option>
                                <option value="HOUR">Hourly (USD/hour)</option>
                                <option value="MONTH">Monthly (USD/month)</option>
                                <option value="DAY">Daily (USD/day)</option>
                            </select>
                        </div>
                    </div>
                    {/* iter 149.3 — Pay disclosure (required). Every posting
                        picks one of three modes so candidates always see a
                        pay signal on the public page. */}
                    <div className="rounded-xl border border-border p-4 space-y-3" data-testid="post-job-pay-block">
                        <div>
                            <Label>Pay disclosure <span className="text-rose-500">*</span></Label>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Simonara requires a pay signal on every posting. Pick one:
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup">
                            {[
                                { v: "range", label: "Range", hint: "e.g., $90k–$130k · Google-for-Jobs best" },
                                { v: "starting_at", label: "Starting at", hint: "e.g., $65k+" },
                                // iter-170 — "Depending on Experience" removed per
                                // California SB1162 + the Browse/Match v1 spec
                                // (block publishing without a pay band). Employers
                                // who need wide latitude should disclose a wide
                                // Range instead.
                            ].map((opt) => {
                                const active = f.pay_mode === opt.v;
                                return (
                                    <button
                                        key={opt.v}
                                        type="button"
                                        onClick={() => setF({ ...f, pay_mode: opt.v })}
                                        data-testid={`post-job-paymode-${opt.v}`}
                                        aria-pressed={active}
                                        className={`text-left rounded-lg border p-3 transition ${active ? "border-emerald-500 bg-emerald-500/10" : "border-border hover:bg-muted/40"}`}
                                    >
                                        <div className="text-sm font-semibold text-foreground">{opt.label}</div>
                                        <div className="mt-0.5 text-[11px] text-muted-foreground">{opt.hint}</div>
                                    </button>
                                );
                            })}
                        </div>
                        {f.pay_mode === "range" && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Minimum ({f.salary_period === "HOUR" ? "hourly USD" : "annual USD"})</Label>
                                    <Input type="number" min="1" data-testid="post-job-salmin" value={f.salary_min} onChange={update("salary_min")} placeholder={f.salary_period === "HOUR" ? "22" : "90000"} className="mt-1.5" />
                                </div>
                                <div>
                                    <Label>Maximum</Label>
                                    <Input type="number" min="1" data-testid="post-job-salmax" value={f.salary_max} onChange={update("salary_max")} placeholder={f.salary_period === "HOUR" ? "32" : "130000"} className="mt-1.5" />
                                </div>
                            </div>
                        )}
                        {f.pay_mode === "starting_at" && (
                            <div>
                                <Label>Starting salary ({f.salary_period === "HOUR" ? "hourly USD" : "annual USD"})</Label>
                                <Input type="number" min="1" data-testid="post-job-salmin" value={f.salary_min} onChange={update("salary_min")} placeholder={f.salary_period === "HOUR" ? "22" : "65000"} className="mt-1.5" />
                                <p className="mt-1 text-[11px] text-muted-foreground">Renders on the public page as "Starting at ${f.salary_min || "—"}{f.salary_period === "HOUR" ? "/hr" : "/yr"}".</p>
                            </div>
                        )}
                        {/* iter 149.6 — Salary insights tooltip. Debounced,
                            only rendered when we have a signal to share. */}
                        {insightBadge && (
                            <div
                                className="mt-2 text-[11px] text-muted-foreground inline-flex items-start gap-1.5"
                                data-testid="post-job-salary-insight"
                            >
                                <Info size={12} className="text-emerald-500 shrink-0 mt-0.5" />
                                <span>{insightBadge}</span>
                            </div>
                        )}
                        {f.pay_mode === "doe" && (
                            <>
                                <p className="text-xs text-muted-foreground italic" data-testid="post-job-paymode-doe-note">
                                    The public page will show <strong className="text-foreground">"Depending on Experience"</strong> instead of a range. Note: Google for Jobs favours ranges — DOE postings tend to rank lower.
                                </p>
                                {showTransparencyNudge && (
                                    <div
                                        className="mt-2 rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 flex items-start gap-2"
                                        data-testid="post-job-transparency-nudge"
                                    >
                                        <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                        <div className="text-xs">
                                            <div className="font-semibold text-foreground" data-testid="post-job-transparency-state">
                                                {PAY_RANGE_STATES[detectedState]} requires a pay range on job postings
                                            </div>
                                            <p className="mt-0.5 text-muted-foreground leading-relaxed">
                                                Roles located in {PAY_RANGE_STATES[detectedState]} must disclose the expected pay range in the posting itself.
                                                Consider switching to <button type="button" onClick={() => setF({ ...f, pay_mode: "range" })} className="underline text-emerald-600 hover:text-emerald-500 font-semibold" data-testid="post-job-transparency-switch">Range</button> or <button type="button" onClick={() => setF({ ...f, pay_mode: "starting_at" })} className="underline text-emerald-600 hover:text-emerald-500 font-semibold">Starting at</button> to stay compliant.
                                                Simonara doesn't provide legal advice — check with your counsel if unsure.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea data-testid="post-job-desc" value={f.description} onChange={update("description")} rows={7} placeholder={"Overview\n\nResponsibilities\n\nQualifications"} required className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Benefits <span className="text-muted-foreground text-xs">(one per line)</span></Label>
                        <Textarea data-testid="post-job-benefits" value={f.benefits} onChange={update("benefits")} rows={4} placeholder={"Health & dental\n401(k) match\nFlexible PTO"} className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Apply URL <span className="text-muted-foreground text-xs">(optional — leave blank for Simonara-hosted apply)</span></Label>
                        <Input data-testid="post-job-apply" value={f.apply_url} onChange={update("apply_url")} placeholder="https://…" className="mt-1.5" />
                    </div>
                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                        <MatchFieldsSection
                            requiredSkills={requiredSkills}
                            setRequiredSkills={setRequiredSkills}
                            knockoutQuestions={knockoutQuestions}
                            setKnockoutQuestions={setKnockoutQuestions}
                        />
                    </div>
                    <button type="submit" disabled={saving || !f.title || !f.location} data-testid="post-job-submit" className="btn-green w-full disabled:opacity-60">
                        {saving ? <><Loader2 size={16} className="mr-2 inline animate-spin" /> Submitting…</> : "Submit for Simonara review"}
                    </button>
                </div>
            </form>

            <aside className="lg:col-span-1 space-y-4">
                {/* Google-for-Jobs SEO Readiness — live-scored as the employer types. */}
                <div data-testid="seo-score-card" className={`card-soft p-5 ${tone.bg} ring-1 ${tone.ring}`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="overline">SEO Readiness</p>
                            <p className={`mt-1 text-3xl font-heading font-medium ${tone.text}`}>
                                <span data-testid="seo-score-value">{seo.score}</span>
                                <span className="text-base text-muted-foreground">/100</span>
                            </p>
                        </div>
                        <div className={`h-10 w-10 rounded-full ${tone.bg} ring-1 ${tone.ring} flex items-center justify-center`}>
                            <Sparkles size={16} className={tone.text} />
                        </div>
                    </div>
                    <p className={`mt-2 text-xs font-semibold ${tone.text}`}>{tone.label}</p>
                    <ul className="mt-4 space-y-2 text-xs">
                        {seo.checks.map((c) => (
                            <li key={c.id} data-testid={`seo-check-${c.id}`} className="flex items-start gap-2">
                                {c.ok ? (
                                    <CheckCircle2 size={12} className="mt-0.5 text-emerald-500 shrink-0" />
                                ) : (
                                    <AlertCircle size={12} className="mt-0.5 text-amber-500 shrink-0" />
                                )}
                                <div className="min-w-0">
                                    <div className={c.ok ? "text-foreground" : "text-foreground/80"}>{c.label}</div>
                                    {!c.ok && c.tip && (
                                        <div className="text-muted-foreground text-[11px] leading-tight">{c.tip}</div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="card-soft p-5 sticky top-6" data-testid="job-formatting-hints">
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                        <Sparkles size={16} /> <span className="text-sm font-semibold">SEO formatting tips</span>
                    </div>
                    <ul className="mt-3 space-y-3 text-sm text-muted-foreground">
                        <li>
                            <strong className="text-foreground">Title format:</strong> <em>Level + Role Name</em> only. Skip company / recruiter jargon.<br />
                            <span className="text-xs">✅ &quot;Senior HR Business Partner&quot;<br />❌ &quot;URGENT: HRBP Rockstar 🔥&quot;</span>
                        </li>
                        <li>
                            <strong className="text-foreground">Location:</strong> Use the format <em>City, ST</em> (or write <em>Remote</em>) — matches Google&apos;s expected schema.
                        </li>
                        <li>
                            <strong className="text-foreground">Description sections:</strong> Overview → Responsibilities → Qualifications → About us. Aim for 250-800 words.
                        </li>
                        <li>
                            <strong className="text-foreground">Salary range:</strong> Providing min &amp; max boosts Google-for-Jobs ranking significantly.
                        </li>
                        <li>
                            <strong className="text-foreground">Benefits:</strong> Bullet a few concrete items — they surface as chips on the public page.
                        </li>
                    </ul>
                    <div className="mt-4 rounded-lg bg-muted/60 p-3 flex gap-2 text-xs text-muted-foreground">
                        <Info size={14} className="shrink-0 mt-0.5" />
                        A Simonara admin reviews every submission for quality &amp; accuracy. Typical turnaround: within one business day.
                    </div>
                </div>

                {/* Compliance trust strip — surfaces here (not on the public job page)
                    so recruiters see the trust signals at the moment they're posting
                    a role. Nothing regulatory-binding; it's a plain-English reminder
                    that any downstream background-check ordering goes through
                    Simonara's compliance-first pipeline. */}
                <div
                    data-testid="employer-compliance-badge"
                    className="card-soft p-4 border-emerald-500/30 bg-emerald-500/[0.03]"
                >
                    <div className="inline-flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                        <ShieldCheck size={14} />
                        <span className="text-sm font-semibold">FCRA + Fair Chance Act compliant · Checkr powered</span>
                    </div>
                    <ul className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                        <li className="flex items-start gap-1.5">
                            <CheckCircle2 size={11} className="mt-0.5 text-emerald-500 shrink-0" />
                            <span>Every background check runs on <strong className="text-foreground">Checkr</strong>, an FCRA-compliant US Consumer Reporting Agency.</span>
                        </li>
                        <li className="flex items-start gap-1.5">
                            <CheckCircle2 size={11} className="mt-0.5 text-emerald-500 shrink-0" />
                            <span><strong className="text-foreground">Consent-first:</strong> candidates sign a standalone disclosure through Checkr before any check runs.</span>
                        </li>
                        <li className="flex items-start gap-1.5">
                            <CheckCircle2 size={11} className="mt-0.5 text-emerald-500 shrink-0" />
                            <span><strong className="text-foreground">Fair Chance Act:</strong> checks only after a conditional offer — enforced at order time.</span>
                        </li>
                        <li className="flex items-start gap-1.5">
                            <CheckCircle2 size={11} className="mt-0.5 text-emerald-500 shrink-0" />
                            <span><strong className="text-foreground">Adverse action</strong> follows the two-step FCRA process with a 5-business-day dispute window.</span>
                        </li>
                    </ul>
                    <Link
                        to="/for-employers/compliance"
                        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
                        data-testid="employer-compliance-badge-link"
                    >
                        See how compliance works →
                    </Link>
                </div>
            </aside>
        </div>
    );
}
